import { isEmpty } from "lodash";
import { FieldInputProps } from "react-final-form";

import {
  AccountCircle,
  AlertSpam,
  ArrowUpward,
  Automation,
  Book,
  CheckCircle,
  EmailAt,
  EmailLetter,
  Folder,
  Group,
  Language,
  People,
  Tag,
  ThumbUp,
  Tickets,
  Time,
} from "@livechat/design-system-icons";
import { IconSource } from "@livechat/design-system-react-components";

import { IRuleTrigger } from "store/entities/rules/interfaces";

import { TriggerFieldArrayElement, WorkflowConditionType } from "../../interfaces";

export const TRIGGER_EMPTY_STATE_TEXT = "Select the conditions that must be met.";

export const TRIGGER_ANY_OPTION_KEY = "any";
export const TRIGGER_ANY_AGENT_OPTION_KEY = "anyAgent";
export const TRIGGER_UNASSIGNED_OPTION_KEY = "unassigned";

export const EqualityLabel: { [key in string]: string } = {
  eq: "exactly",
  gt: "more than",
  lt: "less than",
  gte: "more than or exactly",
  lte: "less than or exactly",
};

export const TRIGGER_ICON: Record<WorkflowConditionType, IconSource> = {
  [WorkflowConditionType.TicketCreated]: Tickets,
  [WorkflowConditionType.TicketChanged]: Tickets,
  [WorkflowConditionType.Source]: Folder,
  [WorkflowConditionType.Status]: CheckCircle,
  [WorkflowConditionType.StatusChanged]: CheckCircle,
  [WorkflowConditionType.Priority]: ArrowUpward,
  [WorkflowConditionType.PriorityChanged]: ArrowUpward,
  [WorkflowConditionType.Rating]: ThumbUp,
  [WorkflowConditionType.RatingChanged]: ThumbUp,
  [WorkflowConditionType.Subject]: Book,
  [WorkflowConditionType.NewRequesterMessage]: EmailLetter,
  [WorkflowConditionType.NewPrivateAgentMessage]: EmailLetter,
  [WorkflowConditionType.NewAnyAgentMessage]: EmailLetter,
  [WorkflowConditionType.NewPublicAgentMessage]: EmailLetter,
  [WorkflowConditionType.Tag]: Tag,
  [WorkflowConditionType.TagAdded]: Tag,
  [WorkflowConditionType.TagRemoved]: Tag,
  [WorkflowConditionType.EventCount]: Automation,
  [WorkflowConditionType.Mailbox]: EmailAt,
  [WorkflowConditionType.Team]: Group,
  [WorkflowConditionType.TeamChanged]: Group,
  [WorkflowConditionType.Agent]: AccountCircle,
  [WorkflowConditionType.HasAssignee]: AccountCircle,
  [WorkflowConditionType.AgentChanged]: AccountCircle,
  [WorkflowConditionType.Follower]: People,
  [WorkflowConditionType.FollowerAdded]: People,
  [WorkflowConditionType.FollowerRemoved]: People,
  [WorkflowConditionType.Requester]: AccountCircle,
  [WorkflowConditionType.IsSpam]: AlertSpam,
  [WorkflowConditionType.IsNotSpam]: AlertSpam,
  [WorkflowConditionType.Time]: Time,
  [WorkflowConditionType.Language]: Language,
};

export const TRIGGER_NAME: Record<WorkflowConditionType, string> = {
  [WorkflowConditionType.TicketCreated]: "Ticket created",
  [WorkflowConditionType.TicketChanged]: "Ticket changed",
  [WorkflowConditionType.Source]: "Source",
  [WorkflowConditionType.Status]: "Status",
  [WorkflowConditionType.StatusChanged]: "Status changed",
  [WorkflowConditionType.Priority]: "Priority",
  [WorkflowConditionType.PriorityChanged]: "Priority changed",
  [WorkflowConditionType.Rating]: "Rating",
  [WorkflowConditionType.RatingChanged]: "Rating",
  [WorkflowConditionType.Subject]: "Subject",
  [WorkflowConditionType.NewRequesterMessage]: "New message from requester",
  [WorkflowConditionType.NewPublicAgentMessage]: "New message to requester",
  [WorkflowConditionType.NewPrivateAgentMessage]: "New private note",
  [WorkflowConditionType.NewAnyAgentMessage]: "Any agent message",
  [WorkflowConditionType.Tag]: "Tag",
  [WorkflowConditionType.TagAdded]: "Tag",
  [WorkflowConditionType.TagRemoved]: "Tag",
  [WorkflowConditionType.Language]: "Language",
  [WorkflowConditionType.EventCount]: "Performed events number",
  [WorkflowConditionType.Mailbox]: "Forwarding address",
  [WorkflowConditionType.Team]: "Team",
  [WorkflowConditionType.TeamChanged]: "Team changed",
  [WorkflowConditionType.Agent]: "Agent",
  [WorkflowConditionType.HasAssignee]: "Agent",
  [WorkflowConditionType.AgentChanged]: "Agent changed",
  [WorkflowConditionType.Follower]: "Follower",
  [WorkflowConditionType.FollowerAdded]: "Follower",
  [WorkflowConditionType.FollowerRemoved]: "Follower",
  [WorkflowConditionType.Requester]: "Requester",
  [WorkflowConditionType.IsSpam]: "Ticket is spam",
  [WorkflowConditionType.IsNotSpam]: "Ticket is not spam",
  [WorkflowConditionType.Time]: "Time since",
};

export const changeTrigger = (
  input: FieldInputProps<TriggerFieldArrayElement>,
  newTrigger: Partial<IRuleTrigger>
): void => {
  input.onChange(newTrigger);
};

export const changeTriggerValues = (
  input: FieldInputProps<TriggerFieldArrayElement>,
  newValues: Partial<IRuleTrigger>,
  options: { overwrite: boolean } = { overwrite: false }
): void => {
  input.onChange(options.overwrite ? { type: input.value.type, ...newValues } : { ...input.value, ...newValues });
};

export function isTriggerType<T extends IRuleTrigger>(
  input: FieldInputProps<TriggerFieldArrayElement>,
  type: T["type"]
): input is FieldInputProps<T> {
  return input.value.type === type;
}

export const defaultTriggers: Record<WorkflowConditionType, Partial<IRuleTrigger>> = {
  [WorkflowConditionType.TicketCreated]: { type: "isNew", eq: true },
  [WorkflowConditionType.TicketChanged]: { type: "isNew", eq: false },
  [WorkflowConditionType.Source]: { type: "detailedSourceType", in: [] },
  [WorkflowConditionType.Status]: { type: "status", in: [] },
  [WorkflowConditionType.StatusChanged]: {
    type: "statusChanged",
    oldStatusIn: [],
    newStatusIn: [],
  },
  [WorkflowConditionType.Priority]: { type: "priority", in: [] },
  [WorkflowConditionType.PriorityChanged]: { type: "priorityChanged", oldPriorityIn: [], newPriorityIn: [] },
  [WorkflowConditionType.Rating]: { type: "rating", in: [] },
  [WorkflowConditionType.RatingChanged]: { type: "ratingChanged", newRatingIn: [] },
  [WorkflowConditionType.Subject]: { type: "subject", containsWholePhraseIn: [] },
  [WorkflowConditionType.NewRequesterMessage]: { type: "newMessage", containsWholePhraseIn: [] },
  [WorkflowConditionType.NewPrivateAgentMessage]: {
    type: "newAgentMessage",
    isPrivate: true,
    containsWholePhraseIn: [],
  },
  [WorkflowConditionType.NewAnyAgentMessage]: { type: "newAgentMessage", containsWholePhraseIn: [] },
  [WorkflowConditionType.NewPublicAgentMessage]: {
    type: "newAgentMessage",
    isPrivate: false,
    containsWholePhraseIn: [],
  },
  [WorkflowConditionType.Tag]: { type: "tagID", in: [] },
  [WorkflowConditionType.TagAdded]: { type: "tagAdded", in: [] },
  [WorkflowConditionType.TagRemoved]: { type: "tagRemoved", in: [] },
  [WorkflowConditionType.EventCount]: { type: "eventCount", eq: 1, eventTypeIn: [] },
  [WorkflowConditionType.Mailbox]: {
    type: "sourceMailbox",
    in: [],
  },
  [WorkflowConditionType.Team]: { type: "teamID", in: [] },
  [WorkflowConditionType.TeamChanged]: {
    type: "teamChanged",
    oldTeamIn: [],
    newTeamIn: [],
  },
  [WorkflowConditionType.Agent]: {
    type: "agentID",
    in: [],
    invert: false,
  },
  [WorkflowConditionType.HasAssignee]: {
    type: "hasAssignee",
    eq: true,
  },
  [WorkflowConditionType.AgentChanged]: {
    type: "agentChanged",
    oldAgentIn: [],
    newAgentIn: [],
  },
  [WorkflowConditionType.Follower]: {
    type: "followerID",
    in: [],
  },
  [WorkflowConditionType.FollowerAdded]: {
    type: "followerAdded",
    in: [],
  },
  [WorkflowConditionType.FollowerRemoved]: {
    type: "followerRemoved",
    in: [],
  },
  [WorkflowConditionType.Requester]: { type: "requesterEmail", in: [] },
  [WorkflowConditionType.IsSpam]: { type: "isSpam", eq: true },
  [WorkflowConditionType.IsNotSpam]: { type: "isSpam", eq: false },
  [WorkflowConditionType.Time]: {
    type: "secondsPassedSinceEvent",
    eq: 60,
    eventTypeIn: [],
  },
  [WorkflowConditionType.Language]: { type: "language", in: [] },
};

export const mapTriggerToTriggerType = (trigger: TriggerFieldArrayElement): WorkflowConditionType | undefined => {
  if (isEmpty(trigger)) {
    return undefined;
  }

  switch (trigger.type) {
    case "isNew":
      return trigger.eq ? WorkflowConditionType.TicketCreated : WorkflowConditionType.TicketChanged;
    case "detailedSourceType":
      return WorkflowConditionType.Source;
    case "status":
      return WorkflowConditionType.Status;
    case "statusChanged":
      return WorkflowConditionType.StatusChanged;
    case "subject":
      return WorkflowConditionType.Subject;
    case "newMessage":
      return WorkflowConditionType.NewRequesterMessage;
    case "eventCount":
      return WorkflowConditionType.EventCount;
    case "teamID":
      return WorkflowConditionType.Team;
    case "agentID":
      return WorkflowConditionType.Agent;
    case "hasAssignee":
      return WorkflowConditionType.HasAssignee;
    case "requesterEmail":
      return WorkflowConditionType.Requester;
    case "tagID":
      return WorkflowConditionType.Tag;
    case "tagAdded":
      return WorkflowConditionType.TagAdded;
    case "tagRemoved":
      return WorkflowConditionType.TagRemoved;
    case "language":
      return WorkflowConditionType.Language;
    case "followerID":
      return WorkflowConditionType.Follower;
    case "followerAdded":
      return WorkflowConditionType.FollowerAdded;
    case "followerRemoved":
      return WorkflowConditionType.FollowerRemoved;
    case "isSpam":
      return trigger.eq ? WorkflowConditionType.IsSpam : WorkflowConditionType.IsNotSpam;
    case "secondsPassedSinceEvent":
      return WorkflowConditionType.Time;
    case "newAgentMessage":
      return trigger.isPrivate !== undefined
        ? trigger.isPrivate
          ? WorkflowConditionType.NewPrivateAgentMessage
          : WorkflowConditionType.NewPublicAgentMessage
        : WorkflowConditionType.NewAnyAgentMessage;
    case "priority":
      return WorkflowConditionType.Priority;
    case "priorityChanged":
      return WorkflowConditionType.PriorityChanged;
    case "sourceMailbox":
      return WorkflowConditionType.Mailbox;
    case "teamChanged":
      return WorkflowConditionType.TeamChanged;
    case "agentChanged":
      return WorkflowConditionType.AgentChanged;
    case "rating":
      return WorkflowConditionType.Rating;
    case "ratingChanged":
      return WorkflowConditionType.RatingChanged;
    default:
      // eslint-disable-next-line no-console
      console.error("Unknown trigger type", trigger);
      return undefined;
  }
};
